import { createContext, useContext, useState, type ReactNode } from "react";

const STORAGE_KEY = "yeschef_onboarding";

export interface OnboardingInvite {
  email: string;
  roleId: number;
}

export interface OnboardingState {
  orgName: string;
  orgCountry: string;
  orgTimezone: string;
  companyNames: string[];
  regionsByCompany: Record<string, string[]>;
  locationsByRegion: Record<string, string[]>;
  invites: OnboardingInvite[];
}

const DEFAULT_STATE: OnboardingState = {
  orgName: "",
  orgCountry: "",
  orgTimezone: "",
  companyNames: [],
  regionsByCompany: {},
  locationsByRegion: {},
  invites: [],
};

function loadState(): OnboardingState {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_STATE;
    return { ...DEFAULT_STATE, ...JSON.parse(raw) };
  } catch {
    return DEFAULT_STATE;
  }
}

interface OnboardingContextValue extends OnboardingState {
  setOrgInfo: (name: string, country: string, timezone: string) => void;
  setCompanyNames: (names: string[]) => void;
  setRegionsByCompany: (regions: Record<string, string[]>) => void;
  setLocationsByRegion: (locs: Record<string, string[]>) => void;
  setInvites: (invites: OnboardingInvite[]) => void;
  clearOnboarding: () => void;
}

const OnboardingContext = createContext<OnboardingContextValue | null>(null);

export function OnboardingProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<OnboardingState>(loadState);

  const update = (patch: Partial<OnboardingState>) => {
    setState((prev) => {
      const next = { ...prev, ...patch };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
      return next;
    });
  };

  return (
    <OnboardingContext
      value={{
        ...state,
        setOrgInfo: (name, country, timezone) =>
          update({ orgName: name, orgCountry: country, orgTimezone: timezone }),
        setCompanyNames: (names) => update({ companyNames: names }),
        setRegionsByCompany: (regions) => update({ regionsByCompany: regions }),
        setLocationsByRegion: (locs) => update({ locationsByRegion: locs }),
        setInvites: (invites) => update({ invites }),
        clearOnboarding: () => {
          localStorage.removeItem(STORAGE_KEY);
          setState(DEFAULT_STATE);
        },
      }}
    >
      {children}
    </OnboardingContext>
  );
}

export function useOnboarding() {
  const ctx = useContext(OnboardingContext);
  if (!ctx) throw new Error("useOnboarding must be used inside OnboardingProvider");
  return ctx;
}
